import { differenceInDays } from "date-fns";
import type { FunctionComponentWithRef, ReactNode } from "react";
import React from "react";
import { graphql, useFragment } from "react-relay";

import { parseDate } from "shared/lib/utils";

import { useSeriesArticleSectionTag } from "scmp-app/components/article/article-render/series-article/hooks";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { seriesBannerContent$key } from "scmp-app/queries/__generated__/seriesBannerContent.graphql";

import {
  Container,
  Content,
  CoverImage,
  CoverImageContainer,
  Headline,
  NewTag,
  SeriesArticleSectionTag,
  StyledBaseLink,
  Summary,
  UpdatedDate,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: seriesBannerContent$key;
  share: ReactNode;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  ref: forwardReference,
  reference: reference_,
  share,
  topicLinkVariant,
}) => {
  const content = useFragment(
    graphql`
      fragment seriesBannerContent on Content {
        ...hooksSeriesArticleSectionTagArticle
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withCoverImage: true
            withCoverImageSize540x360: true
            withCoverImageLandscape250x99: true
            withHeadline: true
            withSummary: true
            withUpdatedDate: true
          )
        publishedDate
      }
    `,
    reference_,
  );
  const providers = useContentItemProviders(content, topicLinkVariant);
  const headline = providers.headline({
    componentTag: "h1",
    hideTooltip: true,
    preferSocialHeadline: true,
  });
  const seriesArticleSectionTag = useSeriesArticleSectionTag(content);

  const isShowNewSeries = () => {
    if (!content.publishedDate) return false;
    const diff = differenceInDays(new Date(), parseDate(content.publishedDate));
    return diff <= 30;
  };

  return (
    <Container className={className} ref={forwardReference}>
      <Content>
        <SeriesArticleSectionTag>
          <StyledBaseLink as="/series" pathname="/series">
            {isShowNewSeries() && <NewTag>NEW</NewTag>}
            {seriesArticleSectionTag}
          </StyledBaseLink>
        </SeriesArticleSectionTag>

        <Headline>{headline}</Headline>

        <Summary>
          {providers.summary({
            isTextOverflowEllipsis: true,
          })}
        </Summary>
        {share}
        <UpdatedDate>Updated: {providers.updatedDate()}</UpdatedDate>
      </Content>
      <CoverImageContainer>
        <CoverImage>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "landscape250x99",
              mobileUp: "size540x360",
              tabletUp: "landscape250x99",
            },
          })}
        </CoverImage>
      </CoverImageContainer>
    </Container>
  );
};

Component.displayName = "ContentItemSeriesBanner";

export const ContentItemSeriesBanner = Component;
